import { useState, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { j219Api, FJ740, FJ741, CreateJ219Request, UpdateJ219Request } from '../../../services/j219Api';
import { J219Mode } from './useJ219Mode';

// Helper: date string -> input (YYYY-MM-DD)
const dateToInput = (v: string | null | undefined): string => (v ? v.substring(0, 10) : '');
const inputToDate = (v: string): string | null => (v ? v + 'T00:00:00Z' : null);

/** Build a blank master record for form use. */
function blankMaster(): Partial<FJ740> {
  return {
    fj740_01: '', fj740_11: '', fj740_12: null, fj740_13: '', fj740_14: '',
    fj740_15: 0, fj740_161: '', fj740_162: '', fj740_17: '', fj740_171: '', fj740_172: '',
    fj740_18: 0, fj740_181: 0, fj740_19: '', fj740_191: '',
    // Page2 yarn layer 1
    fj740_201: 0, fj740_202: '', fj740_203: '', fj740_204: 0, fj740_205: 0,
    fj740_206: 0, fj740_207: '', fj740_208: '',
    // Page2 yarn layer 2
    fj740_211: 0, fj740_212: '', fj740_213: '', fj740_214: 0, fj740_215: 0,
    fj740_216: 0, fj740_217: '', fj740_218: '',
    // Page2 yarn layer 3
    fj740_221: 0, fj740_222: '', fj740_223: '', fj740_224: 0, fj740_225: 0,
    fj740_226: 0, fj740_227: '', fj740_228: '',
    // Page2 weaving
    fj740_23: '', fj740_231: 0, fj740_232: 0, fj740_233: 0, fj740_234: 0,
    fj740_241: 0, fj740_242: 0, fj740_251: 0, fj740_252: 0, fj740_253: 0,
    fj740_261: 0, fj740_262: 0, fj740_27: '', fj740_281: 0, fj740_282: '', fj740_29: '', fj740_291: '',
    // Page3 dyeing layer 1-3
    fj740_301: '', fj740_302: 0, fj740_303: 0, fj740_304: '', fj740_305: 0, fj740_306: '', fj740_307: '',
    fj740_311: '', fj740_312: 0, fj740_313: 0, fj740_314: '', fj740_315: 0, fj740_316: '', fj740_317: '',
    fj740_321: '', fj740_322: 0, fj740_323: 0, fj740_324: '', fj740_325: 0, fj740_326: '', fj740_327: '',
    // Page3 finished product
    fj740_41: '', fj740_42: 0, fj740_43: '', fj740_44: '', fj740_45: 0,
    fj740_51: '', fj740_521: 0, fj740_522: 0, fj740_531: 0, fj740_532: 0, fj740_533: 0,
    fj740_541: 0, fj740_542: 0, fj740_55: '', fj740_561: 0, fj740_562: '',
    fj740_57: '', fj740_571: '', fj740_581: 0, fj740_582: 0, fj740_583: 0, fj740_584: 0,
    // Page3 physical reports
    fj740_61: '', fj740_611: '', fj740_612: '',
    fj740_62: '', fj740_63: '', fj740_64: '', fj740_65: '',
    // Management
    fj740_76: '', fj740_77: null, fj740_78: null, fj740_79: 0, fj740_80: 'N',
  };
}

export interface UseJ219RecordReturn {
  records: FJ740[];
  record: FJ740 | null;
  currentIdx: number;
  formData: Record<string, unknown>;
  details: FJ741[];
  loading: boolean;
  getField: (key: string) => unknown;
  setField: (key: string, value: unknown) => void;
  setDetails: (d: FJ741[]) => void;
  loadList: () => Promise<void>;
  loadRecord: (id: string) => Promise<void>;
  navFirst: () => void;
  navPrev: () => void;
  navNext: () => void;
  navLast: () => void;
  handleAdd: () => void;
  handleEdit: () => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  handleDelete: () => Promise<void>;
  quickSearch: (keyword: string) => Promise<void>;
}

export function useJ219Record(
  mode: J219Mode,
  enterAdd: () => void,
  enterEdit: () => void,
  enterBrowse: () => void,
): UseJ219RecordReturn {
  const [records, setRecords] = useState<FJ740[]>([]);
  const [allIds, setAllIds] = useState<string[]>([]);
  const [record, setRecord] = useState<FJ740 | null>(null);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [formData, setFormData] = useState<Record<string, unknown>>(blankMaster());
  const [details, setDetails] = useState<FJ741[]>([]);
  const [loading, setLoading] = useState(false);
  const initialLoadDone = useRef(false);

  const getField = useCallback((key: string): unknown => formData[key], [formData]);
  const setField = useCallback((key: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Populate form from a record */
  const populateForm = useCallback((rec: FJ740) => {
    const fd: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rec)) {
      if (k === 'details') continue;
      // Convert date fields
      if (k === 'fj740_12' || k === 'fj740_77' || k === 'fj740_78' || k === 'fj740_92') {
        fd[k] = dateToInput(v as string | null);
      } else {
        fd[k] = v;
      }
    }
    setFormData(fd);
    setDetails(rec.details || []);
  }, []);

  const loadList = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await j219Api.list({ page: 1, size: 500, keyword });
      const list = res.data || [];
      setRecords(list);
      setAllIds(list.map(r => r.fj740_01));
      if (list.length > 0) {
        // Load full record with details
        const full = await j219Api.getById(list[0].fj740_01);
        setRecord(full);
        setCurrentIdx(0);
        populateForm(full);
      } else {
        setRecord(null);
        setCurrentIdx(-1);
        setFormData(blankMaster());
        setDetails([]);
      }
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, [populateForm]);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const full = await j219Api.getById(id);
      setRecord(full);
      populateForm(full);
    } catch {
      message.error('載入記錄失敗');
    } finally {
      setLoading(false);
    }
  }, [populateForm]);

  // Initial load
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      loadList();
    }
  }, [loadList]);

  // Navigation
  const navigateTo = useCallback(async (idx: number) => {
    if (idx < 0 || idx >= allIds.length) return;
    setCurrentIdx(idx);
    setLoading(true);
    try {
      const full = await j219Api.getById(allIds[idx]);
      setRecord(full);
      populateForm(full);
    } catch {
      message.error('導覽失敗');
    } finally {
      setLoading(false);
    }
  }, [allIds, populateForm]);

  const navFirst = useCallback(() => navigateTo(0), [navigateTo]);
  const navPrev = useCallback(() => navigateTo(currentIdx - 1), [navigateTo, currentIdx]);
  const navNext = useCallback(() => navigateTo(currentIdx + 1), [navigateTo, currentIdx]);
  const navLast = useCallback(() => navigateTo(allIds.length - 1), [navigateTo, allIds]);

  // Mode actions
  const handleAdd = useCallback(() => {
    setRecord(null);
    setFormData({ ...blankMaster(), fj740_12: new Date().toISOString().substring(0, 10) });
    setDetails([]);
    enterAdd();
  }, [enterAdd]);

  const handleEdit = useCallback(() => {
    if (!record) return;
    populateForm(record);
    enterEdit();
  }, [record, populateForm, enterEdit]);

  const handleCancel = useCallback(() => {
    if (record) {
      populateForm(record);
    } else {
      setFormData(blankMaster());
      setDetails([]);
    }
    enterBrowse();
  }, [record, populateForm, enterBrowse]);

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      // Build master payload
      const master: Partial<FJ740> = {};
      for (const [k, v] of Object.entries(formData)) {
        if (k === 'fj740_01' && mode === 'add') continue;
        if (k === 'fj740_12' || k === 'fj740_77' || k === 'fj740_78') {
          (master as Record<string, unknown>)[k] = inputToDate(v as string);
        } else {
          (master as Record<string, unknown>)[k] = v;
        }
      }
      // Ensure numeric fields are numbers
      const numFields = [
        'fj740_15', 'fj740_18', 'fj740_181',
        'fj740_201', 'fj740_204', 'fj740_205', 'fj740_206',
        'fj740_211', 'fj740_214', 'fj740_215', 'fj740_216',
        'fj740_221', 'fj740_224', 'fj740_225', 'fj740_226',
        'fj740_231', 'fj740_232', 'fj740_233', 'fj740_234',
        'fj740_241', 'fj740_242', 'fj740_251', 'fj740_252', 'fj740_253',
        'fj740_261', 'fj740_262', 'fj740_281',
        'fj740_302', 'fj740_303', 'fj740_305',
        'fj740_312', 'fj740_313', 'fj740_315',
        'fj740_322', 'fj740_323', 'fj740_325',
        'fj740_42', 'fj740_45',
        'fj740_521', 'fj740_522', 'fj740_531', 'fj740_532', 'fj740_533',
        'fj740_541', 'fj740_542', 'fj740_561',
        'fj740_581', 'fj740_582', 'fj740_583', 'fj740_584',
      ];
      for (const nf of numFields) {
        const val = (master as Record<string, unknown>)[nf];
        if (typeof val === 'string') {
          (master as Record<string, unknown>)[nf] = parseFloat(val) || 0;
        }
      }

      const payload: CreateJ219Request = { master: master as Partial<FJ740>, details };
      let saved: FJ740;
      if (mode === 'add' || !record) {
        saved = await j219Api.create(payload);
        message.success(`新增成功：${saved.fj740_01}`);
      } else {
        saved = await j219Api.update(record.fj740_01, payload as UpdateJ219Request);
        message.success('儲存成功');
      }
      // Reload list and navigate to saved record
      const res = await j219Api.list({ page: 1, size: 500 });
      const list = res.data || [];
      setRecords(list);
      setAllIds(list.map(r => r.fj740_01));
      const idx = list.findIndex(r => r.fj740_01 === saved.fj740_01);
      if (idx >= 0) {
        setCurrentIdx(idx);
      }
      setRecord(saved);
      populateForm(saved);
      enterBrowse();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { errors?: { message: string }[] } } };
      const vErrs = apiErr?.response?.data?.errors;
      if (vErrs && vErrs.length > 0) {
        message.error(vErrs.map(e => e.message).join('；'));
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, mode, record, populateForm, enterBrowse]);

  const handleDelete = useCallback(async () => {
    if (!record) return;
    setLoading(true);
    try {
      await j219Api.delete(record.fj740_01);
      message.success(`刪除成功：${record.fj740_01}`);
      await loadList();
      enterBrowse();
    } catch {
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  }, [record, loadList, enterBrowse]);

  const quickSearch = useCallback(async (keyword: string) => {
    await loadList(keyword || undefined);
  }, [loadList]);

  return {
    records, record, currentIdx, formData, details, loading,
    getField, setField, setDetails,
    loadList, loadRecord,
    navFirst, navPrev, navNext, navLast,
    handleAdd, handleEdit, handleSave, handleCancel, handleDelete,
    quickSearch,
  };
}
